import { ChangeDetectionStrategy, Component, Input, OnInit, signal, effect, ElementRef, inject, Injector, ChangeDetectorRef, DestroyRef, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { Menu } from './menu/menu';
import { UserProfile } from './user-profile/user-profile';
import { StatusMessageService } from './status-message/status-message.service';
import { CartOverlayEventsService } from './mini-cart/mini-cart-overlay-events.service';
import { MenuOverlayEventsService } from './menu/menu-overlay-events.service';
import { HeaderService } from './header.service';
import { HeaderResults } from './header-model';
import { MiniCart } from './mini-cart/mini-cart';
import { ErrorData } from '../common/models/error-data';
import { ApiResponse } from '../common/models/api-response.model';
import { firstValueFrom } from 'rxjs';
import { CACHE_KEYS } from '../common/constants/cache-keys';
import { CacheService } from '../common/services/cache-service';
import { MiniCartService } from './mini-cart/mini-cart.service';

import { ElementsService } from '../common/services/elements-service';
import { VehicleContext } from '../landing-page/vehicle-search/vehicle-search-model';
import { VehicleSearchService } from '../landing-page/vehicle-search/vehicle-search-service';
import { VehicleDetails } from "./vehicle-details/vehicle-details";
import { SrpContext, UserContextResponse } from './user-profile/user-profile.model';
import { LoginService } from '../login/login-service';
import { GigyaSessionService } from '../common/services/gigya-session-service';
import { GigyaService2 } from '../common/services/gigya-service2';
import { SrpSnackbarService } from '../common/services/srp-snackbar.service';
import { SRP_ROUTES } from '../common/constants/routes';
import { ReusableService } from '../common/services/reusable-services';
import { eGigyaScript } from '../common/constants/gigya-script-types';
import { mapVehicleContext } from '../common/utils/utils';
import { ContentFilterPipe } from '../common/pipes/content-filter-pipe';
import { COMPONENT_META_DATA } from '../common/constants/component-meta-data';
import { StatusMessage } from './status-message/status-message';
import { Loading } from '../common/components/general/loading/loading';


@Component({
  selector: 'srp-header',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatMenuModule,
    MatBadgeModule,
    Menu,
    UserProfile,
    MiniCart,
    VehicleDetails,
    StatusMessage,
    Loading,
  ],
  providers: [ContentFilterPipe],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'class': 'srp-app-wrapper' }
})
export class Header implements OnInit {
  @Input() appContext: string;
  private readonly elRef = inject(ElementRef);
  private readonly statusMessageService = inject(StatusMessageService);
  private readonly cartEventService = inject(CartOverlayEventsService);
  private readonly menuEventService = inject(MenuOverlayEventsService);
  private readonly headerService = inject(HeaderService);
  private readonly cacheService = inject(CacheService);
  private readonly miniCartService = inject(MiniCartService);
  private readonly elementService = inject(ElementsService);
  private readonly vehicleSearchService = inject(VehicleSearchService);
  private readonly loginService = inject(LoginService);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly gigyaService2 = inject(GigyaService2);
  private readonly gigyaSessionService = inject(GigyaSessionService);
  private readonly snackbarService = inject(SrpSnackbarService);
  private readonly injector = inject(Injector);
  private readonly reusableService = inject(ReusableService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contentPipe = inject(ContentFilterPipe);

  headerState = signal({
    pageContentLabels: [],
    appLinks: null,
    leftImage: null,
    rightImage: null,
    overlayHeadingOperation: '',
    overlayDescriptionOperation: '',
    hasErrors: false
  });
  overlayHeadingOperation = signal("Service Unavailable!");
  overlayDescriptionOperation = signal("The server is temporarily unable to service your request. Please try again later.");
  cartId: string = '';
  headers: Record<string, string> = {};
  vehicleContext: VehicleContext;
  isErcsUser: boolean = false;
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;

  constructor() {
    this.listenAndDispatchCartEvents();
    this.listenToCartEventRequests();
    this.isErcsUser = this.cacheService.getFromCache(CACHE_KEYS.USER.IS_ERCS);
  }

  ngOnInit(): void {
    void this.initializeHeader();
  }

  private async initializeHeader(): Promise<void> {

    // Apply authoritative session vehicle on load (will clear header if session empty)
    // before subscribing to vehicle updates to avoid showing stale cached values.
    await this.refreshVehicleContextFromUserContext();

    // Subscribe after session refresh so route components cannot emit vehicle
    // updates before header listens and avoid cached flicker.
    this.initVehicleContext();

    await this.ensureGigyaScriptLoaded();

    //get appconfig
    await this.getAppConfig();

    //start monitoring session
    console.log('Header initialization');
    this.gigyaSessionService.startMonitoring(this.appContext);

    this.elementService.setAngularContext(this.elRef);

    //Call Header endpoint
    await this.getHeaderData();
  }

  async getAppConfig(): Promise<void> {
    try {
      const response = await this.loginService.getAppConfig();
      if (response?.data && response.data.length > 0) {
        // Handle the app config data
        if (response.data[0].config_key === 'sessionTimeout') {
          this.cacheService.addToCache(CACHE_KEYS.APPCONFIG.SESSION_TIMEOUT, response.data[0].config_value);
        }
      }
    } catch (error) {
      console.error("Error fetching app config in header", error);
    }
  }

  private async ensureGigyaScriptLoaded(): Promise<void> {
    const gigyaWindow = window as any;
    if (gigyaWindow?.gigya?.isReady) {
      return;
    }

    try {
      await this.gigyaService2.loadGigyaScripts(eGigyaScript.MAIN);
    } catch (error) {
      console.error('Failed to load Gigya script in header', error);
    }
  }

  private async refreshVehicleContextFromUserContext(): Promise<void> {
    try {

      let srpContext: SrpContext = this.reusableService.getSrpContextVariable();
      if (!srpContext) {   
        const userContextObservable: UserContextResponse = await this.loginService.getUserContext(true);
        const response = userContextObservable?.data;
        srpContext  = response?.srpContext;
      }
      const vehicleContext = srpContext?.vehicleContext;
      const mappedCartId = srpContext?.userInfo?.commerceContext?.cartId ?? '';

      const mappedVehicle = mapVehicleContext(vehicleContext, mappedCartId);

      this.cartId = mappedCartId || this.cacheService.getFromCache(CACHE_KEYS.USER.CART_ID) || '';

      if (mappedVehicle) {
        this.cacheService.addToCache(CACHE_KEYS.USER.VEHICLE_CONTEXT, mappedVehicle);
        this.vehicleSearchService.updateVehicleDetails(mappedVehicle);
        console.log('Header: applied session vehicle context:', mappedVehicle);
      } else {
        // Clear any existing vehicle context when session has none
        this.cacheService.removeFromCache(CACHE_KEYS.USER.VEHICLE_CONTEXT);
        this.vehicleSearchService.updateVehicleDetails(null);
        console.log('Header: session returned no vehicle; cleared header vehicle context');
      }

      this.cacheService.addToCache(CACHE_KEYS.USER.COMMERCE_CONTEXT_CART_ID, this.cartId);
      this.cacheService.addToCache(CACHE_KEYS.USER.CART_ID, this.cartId);
      this.cdr.detectChanges();
    } catch (err) {
      console.warn('Header: failed to refresh vehicle context from user context', err);
    }
  }

  redirectToLandingPage() {
    const link = `/${SRP_ROUTES.LANDINGPAGE.PATH}`;
    this.reusableService.navigateToUrl(link, this.appContext, 'landing-page');
  }

  private listenAndDispatchCartEvents(): void {
    // Listen for cart close
    effect(() => {
      if (this.cartEventService.closeCart()) {
        const event = new CustomEvent('headerCloseCart', {
          bubbles: true,
          composed: true,
        });
        this.elRef.nativeElement.dispatchEvent(event);
      }
    }, { injector: this.injector });

    // Listen for link click
    effect(() => {
      let linkDetails = this.menuEventService.linkClick();
      if (linkDetails) {
        const event = new CustomEvent('headerLinkClick', {
          detail: linkDetails,
          bubbles: true,
          composed: true,
        });
        this.elRef.nativeElement.dispatchEvent(event);
      }
    }, { injector: this.injector });
  }

  private listenToCartEventRequests(): void {
    this.elRef.nativeElement.addEventListener(
      'addToCart',
      async (event: Event) => {

        //Call cart count if active cart exists
        this.cartId = this.cacheService.getFromCache(CACHE_KEYS.USER.CART_ID) || '';
        if (this.cartId && this.cartId != '') {
          await this.getCartItemsCount(this.cartId);
        }
      }
    );

    this.elRef.nativeElement.addEventListener(
      'updatedVehicleContext',
      async (event: Event) => {

        // Refresh and apply the authoritative session vehicle context. This
        // will set the header vehicle when session has data, or clear it when
        // the session returns empty — avoiding random/old vehicles after reload.
        await this.refreshVehicleContextFromUserContext();

        if (this.cartId && this.cartId != '') {
          await this.getCartItemsCount(this.cartId);
          const cacheCartId = this.cacheService.getFromCache(CACHE_KEYS.USER.CART_ID);
          console.log('Cart ID from API:', this.cartId, 'Cart ID from Cache:', cacheCartId);

          //Removing this condition, as this message needs to be displayed everytime vehicle context is changed
          // if (this.cartId !== cacheCartId) {
          setTimeout(() => {  
            this.snackbarService.show({
              message: this.contentPipe.transform(this.headerState().pageContentLabels, this.LABELS.CART_UPDATED_MSG) || "Vehicle Context Changed. New cart created.",
              type: 'alert',
              duration: 4000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }, 2000);
        }

        //UpdateActive cart Id
        this.cacheService.addToCache(CACHE_KEYS.USER.COMMERCE_CONTEXT_CART_ID, this.cartId);
        this.cacheService.addToCache(
          CACHE_KEYS.USER.CART_ID,
          this.cartId
        );
      }
    );
  }

  async getHeaderData() {
    try {
      // Ensure supported languages are cached BEFORE calling getHeader().
      // The header component initialises before the page resolver runs, so
      // getApplicableLanguage() can return undefined, causing getHeader() to
      // throw silently and leaving pageContentLabels empty (no translations).
      await firstValueFrom(this.reusableService.getSupportedLanguages());

      const headerResponse: ApiResponse<HeaderResults> = await firstValueFrom(
        this.headerService.getHeader()
      );
      if (!headerResponse?.data) {
        this.headerState.set({ ...this.headerState(), hasErrors: true });
      } else {

        this.headerState.set({
          ...this.headerState(),
          pageContentLabels: headerResponse.data.labels,
          appLinks: headerResponse.data.appLinks,
          leftImage: !this.isErcsUser ? headerResponse.data.header?.desktopBlueLogo : headerResponse.data.header?.desktopErcsBlueLogo,
          rightImage: !this.isErcsUser ? headerResponse.data.header?.desktopCatalogLogo : headerResponse.data.header?.desktopErcsCatalogLogo,
          hasErrors: false,
        });

        // Pass page content labels to session service for timeout dialog
        this.gigyaSessionService.setPageContent?.(headerResponse.data.labels);

        //Call cart count if active cart exist
        this.cartId = this.cacheService.getFromCache(CACHE_KEYS.USER.CART_ID) || '';
        if (this.cartId && this.cartId != '') {
          await this.getCartItemsCount(this.cartId);
        }
      }
    } catch (error: any) {
      console.error("Error fetching the header api response", error);
      this.headerState.set({ ...this.headerState(), hasErrors: true });
    }
  }

  initVehicleContext(): void {
    this.vehicleSearchService.getVehicleDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((details: VehicleContext | null) => {
        this.vehicleContext = details ?? this.getFromCacheVehicleContext();
        console.log('Vehicle context initialized in header:', this.vehicleContext);
        this.cdr.detectChanges();
      });
  }

  private getFromCacheVehicleContext(): VehicleContext | null {
    const cachedVehicleContext = this.cacheService.getFromCache(CACHE_KEYS.USER.VEHICLE_CONTEXT) as VehicleContext;
    console.log('Retrieved vehicle context from cache:', cachedVehicleContext);
    return cachedVehicleContext ?? null;
  }

  async getCartItemsCount(cartId: string) {
    this.miniCartService.getCartItemsCount(cartId)
      .then((resp) => {
        if (resp?.data) {
          this.miniCartService.updateMiniCartInfo(resp.data);
        }

      })
      .catch((error) => {
      });
  }

  setErrorResponse(errorData: ErrorData) {
    if (errorData) {
      const overlayHeadingOp = errorData.errorList?.[0].errorMsg || "Service Unavailable!";
      const overlayDescriptionOp = "The server is temporarily unable to service your request. Please try again later.";
      this.overlayDescriptionOperation.set(overlayDescriptionOp);
      this.overlayHeadingOperation.set(overlayHeadingOp);
      this.headerState.set({ ...this.headerState(), hasErrors: true });
    }
  }
}
